import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Form,
  Input,
  InputNumber,
  Button,
  Select,
  message,
  Space,
  Typography,
  Row,
  Col,
  Alert,
  Spin,
  Modal,
  Tag,
  Divider
} from 'antd';
import {
  DollarOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SaveOutlined,
  InfoCircleOutlined
} from '@ant-design/icons';
import axios from 'axios';

const { Title, Text, Paragraph } = Typography;
const { Option } = Select;

const Pricing = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pricingTiers, setPricingTiers] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [form] = Form.useForm();

  useEffect(() => {
    fetchPricingTiers();
  }, []);

  const fetchPricingTiers = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/api/agency/pricing');
      setPricingTiers(response.data);
    } catch (error) {
      console.error('Error fetching pricing tiers:', error);
      message.error('Failed to load pricing tiers');
    } finally {
      setLoading(false);
    }
  };

  const handleAddTier = () => {
    setEditingTier(null);
    form.resetFields();
    setModalVisible(true);
  };

  const handleEditTier = (tier) => {
    setEditingTier(tier);
    form.setFieldsValue(tier);
    setModalVisible(true);
  };

  const handleDeleteTier = async (tierId) => {
    Modal.confirm({
      title: 'Delete Pricing Tier',
      content: 'Are you sure you want to delete this pricing tier? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      onOk: async () => {
        try {
          await axios.delete(`/api/agency/pricing/${tierId}`);
          message.success('Pricing tier deleted successfully');
          fetchPricingTiers();
        } catch (error) {
          console.error('Error deleting pricing tier:', error);
          message.error('Failed to delete pricing tier');
        }
      }
    });
  };

  const handleSaveTier = async (values) => {
    setSaving(true);
    try {
      if (editingTier) {
        await axios.put(`/api/agency/pricing/${editingTier.id}`, values);
        message.success('Pricing tier updated successfully');
      } else {
        await axios.post('/api/agency/pricing', values);
        message.success('Pricing tier created successfully');
      }
      setModalVisible(false);
      form.resetFields();
      fetchPricingTiers();
    } catch (error) {
      console.error('Error saving pricing tier:', error);
      message.error('Failed to save pricing tier');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      title: 'Tier Name',
      dataIndex: 'name',
      key: 'name',
      render: (text, record) => (
        <Space>
          <Text strong>{text}</Text>
          {record.isDefault && <Tag color="blue">Default</Tag>}
        </Space>
      )
    },
    {
      title: 'Service Type',
      dataIndex: 'serviceType',
      key: 'serviceType',
      render: (text) => <Tag>{text}</Tag>
    },
    {
      title: 'Base Price',
      dataIndex: 'basePrice',
      key: 'basePrice',
      render: (price) => `$${price.toFixed(2)}`
    },
    {
      title: 'Per Location Fee',
      dataIndex: 'perLocationFee',
      key: 'perLocationFee',
      render: (fee) => `$${fee.toFixed(2)}`
    },
    {
      title: 'Setup Fee',
      dataIndex: 'setupFee',
      key: 'setupFee',
      render: (fee) => fee ? `$${fee.toFixed(2)}` : 'None'
    },
    {
      title: 'Billing Cycle',
      dataIndex: 'billingCycle',
      key: 'billingCycle',
      render: (cycle) => <Tag color="green">{cycle}</Tag>
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive) => (
        <Tag color={isActive ? 'success' : 'default'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      )
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEditTier(record)}
          >
            Edit
          </Button>
          <Button
            type="link"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteTier(record.id)}
          >
            Delete
          </Button>
        </Space>
      )
    }
  ];

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '100px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>
        <DollarOutlined /> Pricing Controls
      </Title>
      <Paragraph type="secondary">
        Manage corporate pricing tiers for all locations. Individual locations cannot modify these settings.
      </Paragraph>

      <Alert
        message="Corporate Pricing Management"
        description="All pricing is set at the corporate level and applies to all locations. Locations can view pricing but cannot make changes."
        type="info"
        icon={<InfoCircleOutlined />}
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Card
        title="Pricing Tiers"
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddTier}
          >
            Add Pricing Tier
          </Button>
        }
      >
        <Table
          columns={columns}
          dataSource={pricingTiers}
          rowKey="id"
          pagination={{ pageSize: 10 }}
        />
      </Card>

      <Modal
        title={editingTier ? 'Edit Pricing Tier' : 'Add Pricing Tier'}
        open={modalVisible}
        onCancel={() => {
          setModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={700}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSaveTier}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Tier Name"
                name="name"
                rules={[{ required: true, message: 'Please enter tier name' }]}
              >
                <Input placeholder="e.g., Premium Plan" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Service Type"
                name="serviceType"
                rules={[{ required: true, message: 'Please select service type' }]}
              >
                <Select placeholder="Select service type">
                  <Option value="Campaign Management">Campaign Management</Option>
                  <Option value="Ad Creation">Ad Creation</Option>
                  <Option value="Analytics">Analytics</Option>
                  <Option value="Full Service">Full Service</Option>
                  <Option value="Consulting">Consulting</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={8}>
              <Form.Item
                label="Base Price"
                name="basePrice"
                rules={[{ required: true, message: 'Please enter base price' }]}
              >
                <InputNumber
                  min={0}
                  step={0.01}
                  prefix="$"
                  style={{ width: '100%' }}
                  placeholder="0.00"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Per Location Fee"
                name="perLocationFee"
                rules={[{ required: true, message: 'Please enter per location fee' }]}
              >
                <InputNumber
                  min={0}
                  step={0.01}
                  prefix="$"
                  style={{ width: '100%' }}
                  placeholder="0.00"
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item
                label="Setup Fee"
                name="setupFee"
              >
                <InputNumber
                  min={0}
                  step={0.01}
                  prefix="$"
                  style={{ width: '100%' }}
                  placeholder="0.00"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="Billing Cycle"
                name="billingCycle"
                rules={[{ required: true, message: 'Please select billing cycle' }]}
              >
                <Select placeholder="Select billing cycle">
                  <Option value="Monthly">Monthly</Option>
                  <Option value="Quarterly">Quarterly</Option>
                  <Option value="Annually">Annually</Option>
                  <Option value="One-time">One-time</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                label="Status"
                name="isActive"
                valuePropName="checked"
                initialValue={true}
              >
                <Select placeholder="Select status">
                  <Option value={true}>Active</Option>
                  <Option value={false}>Inactive</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            label="Description"
            name="description"
          >
            <Input.TextArea
              rows={3}
              placeholder="Enter pricing tier description"
            />
          </Form.Item>

          <Form.Item
            label="Set as Default"
            name="isDefault"
            valuePropName="checked"
          >
            <Select placeholder="Set as default">
              <Option value={true}>Yes</Option>
              <Option value={false}>No</Option>
            </Select>
          </Form.Item>

          <Divider />

          <Form.Item>
            <Space>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                loading={saving}
              >
                {editingTier ? 'Update' : 'Create'} Pricing Tier
              </Button>
              <Button
                onClick={() => {
                  setModalVisible(false);
                  form.resetFields();
                }}
              >
                Cancel
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Pricing;
